import React, {useCallback, useEffect, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {Button, Table, TableBody, TableHead, TableRow, Tooltip} from "@mui/material";
import Box from "@mui/material/Box";
import Fab from "@mui/material/Fab";
import AddIcon from "@mui/icons-material/Add";
import UserLayout from "../components/UserLayout";
import {CreateTaskDialog} from "./create-task/createTaskDialog";
import {getAllPaginatedTasksAction} from "../api/task";
import {AppState} from "../store";
import {setAllPaginatedTasks} from "../features/task/taskSlice";
import {Link, useLocation, useNavigate, useSearchParams} from "react-router-dom";
import {
    CASE_PERMISSIONS,
    CaseType,
    getPresentableTaskPriority,
    getPresentableTaskStatus,
    getPresentableTaskType,
    LegalCaseDTO,
    MinimalLegalCaseDTO,
    TASK_PERMISSIONS,
    TaskDTO,
    TaskPriority,
    TaskStatus,
    UserPermission
} from "../api/dtos";
import {ChangeStatusDialog} from "./create-status/changeStatusDialog";
import {Pagination} from "@mui/lab";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import ClearIcon from "@mui/icons-material/Clear";
import TaskAction from "../actions/TaskAction";
import * as XLSX from "xlsx-js-style";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {robotoRegularBase64} from '../shared/pdf-fonts';
import {DeleteTaskDialog} from "./delete-task/deleteTaskDialog";
import {EditTaskDialog} from "./edit-task/editTaskDialog";
import {AsyncTaskStatusType} from "../shared/dtos";
import {adminOrHasPermission, formatDate, NO_DATA} from "../shared/utils";
import {EditTaskAssigneesDialog} from "./edit-task/editTaskAssigneesDialog";
import {AddFileToTaskDialog} from "./add-file-to-task/add-file-to-task";
import TableCell from "@mui/material/TableCell";

export default function Task() {
    const navigate = useNavigate();
    const dispatch: any = useDispatch();
    const location = useLocation();
    const [createTaskDialogOpen, setCreateTaskDialogOpen] = useState(false);
    const [changeStatusDialogOpen, setChangeStatusDialogOpen] = useState(false);
    const [deleteTaskDialogOpen, setDeleteTaskDialogOpen] = useState(false);
    const [activeTask, setActiveTask] = useState<TaskDTO | null>(null);
    const [searchParams] = useSearchParams();
    const page = Number(searchParams.get('page') || "1");
    const allPaginatedTasks = useSelector((state: AppState) => state.task.allPaginatedTasks);
    const [textFilter, setTextFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState<TaskStatus | null>(null);
    const [priorityFilter, setPriorityFilter] = useState<TaskPriority | null>(null);
    const pageRef = useRef(page);
    const textFilterRef = useRef(textFilter);
    const statusFilterRef = useRef(statusFilter);
    const priorityFilterRef = useRef(priorityFilter);
    const currentUser = useSelector((state: AppState) => state.auth.userWithToken?.user);
    const getAllPaginatedTasksStatus = useSelector((state: AppState) => state.task.getAllPaginatedTasksStatus)

    const loadTasks = useCallback(() => {
        dispatch(getAllPaginatedTasksAction({
            legalCaseIdFilter: null,
            contractIdFilter: null,
            userIdFilter: null,
            companyIdFilter: currentUser?.associatedCompany?.id || null,
            textFilter,
            statusFilter,
            priorityFilter,
            page
        }));
    }, [page, textFilter, statusFilter, priorityFilter, dispatch]);

    useEffect(() => {
        loadTasks();
        return () => {
            dispatch(setAllPaginatedTasks(null));
        };
    }, [dispatch]);

    useEffect(() => {
        if (pageRef.current !== page ||
            textFilterRef.current !== textFilter ||
            statusFilterRef.current !== statusFilter ||
            priorityFilterRef.current !== priorityFilter) {
            loadTasks();
            pageRef.current = page;
            textFilterRef.current = textFilter;
            statusFilterRef.current = statusFilter;
            priorityFilterRef.current = priorityFilter;
        }
    }, [page, textFilter, statusFilter, priorityFilter, dispatch]);

    const handleCreateTaskDialogOpen = () => setCreateTaskDialogOpen(true);
    const handleCreateTaskDialogClose = () => setCreateTaskDialogOpen(false);

    const handleChangeStatusDialogOpen = () => setChangeStatusDialogOpen(true);
    const handleChangeStatusDialogClose = () => setChangeStatusDialogOpen(false);

    const handleDeleteTaskDialogOpen = () => setDeleteTaskDialogOpen(true);
    const handleDeleteTaskDialogClose = () => setDeleteTaskDialogOpen(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editAssigneDialogOpen, setEditAssigneDialogOpen] = useState(false);

    const reloadFirstPage = () => {
        if (page === 1) {
            dispatch(getAllPaginatedTasksAction({
                legalCaseIdFilter: null,
                contractIdFilter: null,
                userIdFilter: null,
                companyIdFilter: currentUser?.associatedCompany?.id || null,
                textFilter,
                statusFilter,
                priorityFilter,
                page
            }));
        } else {
            navigate("/gorevler");
        }
    }

    const handleCreateTask = () => {
        handleCreateTaskDialogClose();
        reloadFirstPage();
    }

    const openCreateTask = () => handleCreateTaskDialogOpen();

    const handleEditTask = () => {
        handleEditDialogClose();
        reloadFirstPage();
    };

    const handleEditDialogOpen = (task: TaskDTO) => {
        setActiveTask(task);
        setEditDialogOpen(true);
    };

    const handleEditAssigneesDialogOpen = (task: TaskDTO) => {
        setActiveTask(task);
        setEditAssigneDialogOpen(true);
    };

    const handleEditDialogClose = () => {
        setActiveTask(null);
        setEditDialogOpen(false);
    };

    const handleEditAssigneesDialogClose = () => {
        setActiveTask(null);
        setEditAssigneDialogOpen(false);
    };

    const openChangeStatus = (task: TaskDTO) => {
        setActiveTask(task);
        handleChangeStatusDialogOpen();
    };

    const openDelete = (task: TaskDTO) => {
        setActiveTask(task);
        handleDeleteTaskDialogOpen();
    };

    const handleChangeStatus = () => {
        setTextFilter("");
        setStatusFilter(null);
        setPriorityFilter(null);
        setActiveTask(null);
        handleChangeStatusDialogClose();
        reloadFirstPage();
    };

    const handleDelete = () => {
        setActiveTask(null);
        handleDeleteTaskDialogClose();
        reloadFirstPage();
    }

    const handleEditTaskAssignees = () => {
        setActiveTask(null);
        handleEditAssigneesDialogClose();
        reloadFirstPage();
    }

    const handleChangePage = (_: React.ChangeEvent<unknown>, value: number) => {
        const searchParams = new URLSearchParams(location.search);
        searchParams.set('page', value.toString());
        navigate(`?${searchParams.toString()}`);
    };

    const applyStatusFilter = (e: { target: { value: string; }; }) => {
        const status = e.target.value === "" ? null : (e.target.value as TaskStatus);
        setStatusFilter(status);
        navigate("?page=1");
    };

    const applyTextFilter = (e: { target: { value: any; }; }) => {
        const inputTextSearch = e.target.value;
        setTextFilter(inputTextSearch);
        navigate("?page=1");
    };

    const applyPriorityFilter = (e: { target: { value: string; }; }) => {
        const priority = e.target.value === "" ? null : (e.target.value as TaskPriority);
        setPriorityFilter(priority);
        navigate("?page=1");
    };

    const handleClearPriority = () => {
        setPriorityFilter(null);
    };

    const handleClearStatus = () => {
        setStatusFilter(null);
    };

    const statusOptions = Object.values(TaskStatus);
    const priorityOptions = Object.values(TaskPriority);

    const [addFileDialogOpen, setAddFileDialogOpen] = useState(false);

    const handleAddFileDialogOpen = () => setAddFileDialogOpen(true);
    const handleAddFileDialogClose = () => setAddFileDialogOpen(false);

    const openAddFile = (task: TaskDTO) => {
        setActiveTask(task);
        handleAddFileDialogOpen();
    };

    const handleFileAdded = () => {
        setActiveTask(null);
        handleAddFileDialogClose();
    };

    const getCaseDetailUrl = (legalCase: LegalCaseDTO | MinimalLegalCaseDTO) => {
        switch (legalCase.type) {
            case CaseType.Hukuk:
                return `/hukuk-davalari/${legalCase.id}/ayrintilar`;
            case CaseType.Sorusturma:
                return `/sorusturmalar/${legalCase.id}/ayrintilar`;
            case CaseType.Ceza:
                return `/ceza-davalari/${legalCase.id}/ayrintilar`;
            case CaseType.Icra:
                return `/icra-davalari/${legalCase.id}/ayrintilar`;
            case CaseType.Idari:
                return `/idari-davalari/${legalCase.id}/ayrintilar`;
            default:
                return `/hukuk-davalari/${legalCase.id}/ayrintilar`;
        }
    };

    const exportToExcel = () => {
        const headers = [
            ["GÖREV ADI", "AÇIKLAMA", "ATAYAN KİŞİ", "ATANANAN KULLANICILAR", "DURUM", "İLGİLİ DAVA", "ATANMA TARİHİ", "HEDEFLENEN TARİH", "ÖNCELİK"]
        ];

        const worksheet = XLSX.utils.json_to_sheet(
            (allPaginatedTasks?.entity || []).map((task) => ({
                "Görev Adı": task.title,
                "Açıklama": task.description,
                "Atayan Kişi": task.createdBy ? `${task.createdBy.firstName} ${task.createdBy.lastName}` : '-',
                "Atanan Kullanıcılar": task.assignees.map((user: any) => `${user.firstName} ${user.lastName}`).join(", "),
                "Durum": getPresentableTaskStatus(task.status),
                "İlgili Dava": task.legalCase ? task.legalCase.name : NO_DATA,
                "Atanma Tarihi": formatDate(task.createdAt),
                "Hedeflenen Tarih": task.dueDate ? formatDate(task.dueDate) : NO_DATA,
                "Öncelik": getPresentableTaskPriority(task.priority)
            }))
        );

        const workbook = XLSX.utils.book_new();
        XLSX.utils.sheet_add_aoa(worksheet, headers);

        const headerRow = headers[0];
        const columnWidths = headerRow.map(header => ({wch: header.length + 5}));
        worksheet['!cols'] = columnWidths;
        headerRow.forEach((header, index) => {
            const cellRef = XLSX.utils.encode_cell({r: 0, c: index});
            if (worksheet[cellRef]) {
                worksheet[cellRef].s = {
                    font: {bold: true,},
                    fill: {
                        patternType: "solid",
                        fgColor: {rgb: "D3D3D3"}
                    }
                };
            }
        });

        XLSX.utils.book_append_sheet(workbook, worksheet, "GÖREVLER");
        XLSX.writeFile(workbook, "görevler.xlsx");
    };

    const exportToPDF = () => {
        const doc = new jsPDF();

        doc.addFileToVFS('Roboto-Regular.ttf', robotoRegularBase64);
        doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
        doc.setFont('Roboto');

        doc.setFontSize(18);
        doc.text('GÖREVLER', 14, 20);

        const tableData = (allPaginatedTasks?.entity || []).map((task) => [
            task.title,
            task.description,
            task.createdBy ? `${task.createdBy.firstName} ${task.createdBy.lastName}` : '-',
            task.assignees.map((user: any) => `${user.firstName} ${user.lastName}`).join(", "),
            getPresentableTaskStatus(task.status),
            task.legalCase ? task.legalCase.name : NO_DATA,
            formatDate(task.createdAt),
            task.dueDate ? formatDate(task.dueDate) : NO_DATA,
            getPresentableTaskPriority(task.priority)
        ]);

        const tableColumns = ['GÖREV ADI', 'AÇIKLAMA', 'ATAYAN KİŞİ', 'ATANAN KULLANICILAR', 'DURUM', 'İLGİLİ DAVA', 'ATANMA TARİHİ', 'HEDEFLENEN TARİH', 'ÖNCELİK'];

        autoTable(doc, {
            head: [tableColumns],
            body: tableData,
            startY: 30,
            margin: {horizontal: 10},
            styles: {
                font: 'Roboto',
                cellPadding: 3,
                fontSize: 8,
                valign: 'middle',
                halign: 'left'
            }
        });

        doc.save('gorevler.pdf');
    };

    const getTaskDetailUrl = (task: TaskDTO) => {
        return `/gorevler/${task.id}/ayrintilar`;
    };

    return (
        <UserLayout>
            <div id="sidebar">
                <h1>GÖREVLER</h1>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 1,
                        m: 1,
                        borderRadius: 1,
                    }}>
                    <Box sx={{display: 'flex', gap: 2}}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={exportToExcel}
                            sx={{fontSize: '0.875rem', padding: '8px 16px'}}>
                            Excel'e Dışa Aktar
                        </Button>
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={exportToPDF}
                            sx={{fontSize: '0.875rem', padding: '8px 16px'}}>
                            PDF'e Dışa Aktar
                        </Button>
                    </Box>
                    {adminOrHasPermission(currentUser, UserPermission.TASK_ADD) && (
                        <Fab
                            size="small"
                            color="primary"
                            aria-label="add"
                            sx={{marginLeft: "auto", marginBottom: 5}}
                            onClick={openCreateTask}>
                            <AddIcon/>
                        </Fab>
                    )}
                </Box>
            </div>

            {editDialogOpen && activeTask != null && (
                <EditTaskDialog
                    task={activeTask}
                    handleClose={handleEditDialogClose}
                    handleEditTask={handleEditTask}/>
            )}

            {createTaskDialogOpen && (
                <CreateTaskDialog
                    handleClose={handleCreateTaskDialogClose}
                    handleCreateTask={handleCreateTask}/>
            )}

            {changeStatusDialogOpen && activeTask != null && (
                <ChangeStatusDialog
                    task={activeTask}
                    handleClose={handleChangeStatusDialogClose}
                    handleChangeStatus={handleChangeStatus}/>
            )}

            {deleteTaskDialogOpen && activeTask != null && (
                <DeleteTaskDialog
                    task={activeTask}
                    handleClose={handleDeleteTaskDialogClose}
                    handleDelete={handleDelete}/>
            )}

            {editAssigneDialogOpen && activeTask != null && (
                <EditTaskAssigneesDialog
                    taskId={activeTask.id}
                    handleClose={handleEditAssigneesDialogClose}
                    handleEditTaskAssignees={handleEditTaskAssignees}
                    currentAssigneeIds={activeTask.assignees.map((it) => it.id)}/>
            )}

            {addFileDialogOpen && activeTask != null && (
                <AddFileToTaskDialog
                    task={activeTask}
                    onClose={handleAddFileDialogClose}
                    onFileAdded={handleFileAdded}/>
            )}

            <Box sx={{overflowX: 'auto'}}>
                <Table sx={{minWidth: 800}} aria-label="task table">
                    <TableHead>
                        <TableRow>
                            <TableCell style={{width: "12%"}}>
                                <TextField
                                    margin="dense"
                                    style={{minWidth: "210px"}}
                                    label="Görev Adı"
                                    variant="outlined"
                                    size="small"
                                    onChange={applyTextFilter}/>
                            </TableCell>
                            <TableCell style={{width: "12%"}}/>
                            <TableCell style={{width: "12%"}}/>
                            <TableCell style={{width: "12%"}}/>
                            <TableCell style={{width: "12%"}}>
                                <FormControl variant="outlined" size="small" fullWidth>
                                    <InputLabel id="status-label">Durumu</InputLabel>
                                    <Select
                                        style={{minWidth: "210px"}}
                                        labelId="status-label"
                                        label="Durumu"
                                        value={statusFilter || ""}
                                        onChange={applyStatusFilter}
                                        defaultValue=""
                                        endAdornment={
                                            statusFilter && (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={handleClearStatus}>
                                                        <ClearIcon/>
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }>
                                        {statusOptions.map((status) => (
                                            <MenuItem key={status} value={status}>
                                                {getPresentableTaskStatus(status)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </TableCell>
                            <TableCell style={{width: "12%"}}/>
                            <TableCell style={{width: "12%"}}/>
                            <TableCell style={{width: "12%"}}/>
                            <TableCell style={{width: "12%"}}/>
                            <TableCell style={{width: "12%"}}>
                                <FormControl variant="outlined" size="small" fullWidth>
                                    <InputLabel id="priority-label">Öncelik</InputLabel>
                                    <Select
                                        style={{minWidth: "210px"}}
                                        labelId="priority-label"
                                        label="Öncelik"
                                        value={priorityFilter || ""}
                                        onChange={applyPriorityFilter}
                                        defaultValue=""
                                        endAdornment={
                                            priorityFilter && (
                                                <InputAdornment position="start" style={{marginRight: "10px"}}>
                                                    <IconButton onClick={handleClearPriority}>
                                                        <ClearIcon/>
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }>
                                        {priorityOptions.map((priority) => (
                                            <MenuItem key={priority} value={priority}>
                                                {getPresentableTaskPriority(priority)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </TableCell>
                            <TableCell style={{width: "12%"}}/>
                        </TableRow>
                        <TableRow>
                            <TableCell>GÖREV ADI</TableCell>
                            <TableCell>AÇIKLAMA</TableCell>
                            <TableCell>ATAYAN KİŞİ</TableCell>
                            <TableCell>ATANAN KULLANICILAR</TableCell>
                            <TableCell>DURUMU</TableCell>
                            <TableCell>İLGİLİ DAVA</TableCell>
                            <TableCell>ATANMA TARİHİ</TableCell>
                            <TableCell>HEDEFLENEN TARİH</TableCell>
                            <TableCell>GÖREV TİPİ</TableCell>
                            <TableCell>ÖNCELİK</TableCell>
                            <TableCell>İŞLEMLER</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {allPaginatedTasks?.entity.map((task) => (
                            <TableRow key={task.id}>
                                <TableCell>
                                    {
                                        currentUser &&
                                        (task.assignees.map((it) => it.id).includes(currentUser.id) &&
                                            adminOrHasPermission(currentUser, TASK_PERMISSIONS))
                                            ? (
                                                <Link to={getTaskDetailUrl(task)} className="custom-link">
                                                    {task.title || NO_DATA}
                                                </Link>
                                            ) : task.title || NO_DATA
                                    }
                                </TableCell>
                                <TableCell>{task.description}</TableCell>
                                <TableCell>{task.createdBy ? `${task.createdBy.firstName} ${task.createdBy.lastName}` : NO_DATA}</TableCell>
                                <TableCell>
                                    {task.assignees.length > 0
                                        ? task.assignees.map((user: any) => user.firstName + " " + user.lastName).join(', ')
                                        : 'Atama Yok'}
                                </TableCell>
                                <TableCell>
                                    {task.status === TaskStatus.WaitingForReply ? (
                                        <Tooltip title={task.waitReason || "Bekleme sebebi belirtilmemiş"}>
                                            <span>{getPresentableTaskStatus(task.status)}</span>
                                        </Tooltip>
                                    ) : (
                                        getPresentableTaskStatus(task.status)
                                    )}
                                </TableCell>
                                <TableCell>
                                    {task.legalCase
                                        ? (
                                            currentUser &&
                                            (task.legalCase.responsibles.map((it) => it.id).includes(currentUser.id) &&
                                                adminOrHasPermission(currentUser, CASE_PERMISSIONS))
                                                ? (
                                                    <Link to={getCaseDetailUrl(task.legalCase)} className="custom-link">
                                                        {task.legalCase.name}
                                                    </Link>
                                                )
                                                : task.legalCase.name
                                        ) : (
                                            NO_DATA
                                        )}
                                </TableCell>
                                <TableCell>{
                                    task.createdAt
                                        ? formatDate(task.createdAt)
                                        : NO_DATA
                                }</TableCell>
                                <TableCell>{
                                    task.dueDate
                                        ? formatDate(task.dueDate)
                                        : NO_DATA
                                }</TableCell>
                                <TableCell>{getPresentableTaskType(task.type)}</TableCell>
                                <TableCell>{getPresentableTaskPriority(task.priority)}</TableCell>
                                <TableCell>
                                    <TaskAction
                                        onEditTask={() => handleEditDialogOpen(task)}
                                        task={task}
                                        onChangeStatus={openChangeStatus}
                                        onDelete={openDelete}
                                        onEditAssignees={() => handleEditAssigneesDialogOpen(task)}
                                        onAddFile={openAddFile}/>
                                </TableCell>
                            </TableRow>
                        ))}
                        {getAllPaginatedTasksStatus?.type !== AsyncTaskStatusType.Loading && allPaginatedTasks?.numberOfElements === 0 && (
                            <TableRow>
                                <TableCell colSpan={11} style={{textAlign: "center"}}>
                                    Görevler Bulunamadı
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </Box>

            {allPaginatedTasks?.totalNumberPages != null && allPaginatedTasks.totalNumberPages > 1 && (
                <div style={{display: "flex", justifyContent: "center", marginTop: "10px"}}>
                    <Pagination
                        page={page}
                        count={allPaginatedTasks.totalNumberPages}
                        defaultPage={1}
                        onChange={handleChangePage}/>
                </div>
            )}
        </UserLayout>
    );
}
